import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";

const Market = () => {
  const navigate = useNavigate();
  const [selectedSymbol, setSelectedSymbol] = useState("BTCUSDT");
  const [searchQuery, setSearchQuery] = useState("");

  const popularPairs = [
    { symbol: "BTCUSDT", name: "Bitcoin", short: "BTC/USDT" },
    { symbol: "ETHUSDT", name: "Ethereum", short: "ETH/USDT" },
    { symbol: "BNBUSDT", name: "BNB", short: "BNB/USDT" },
    { symbol: "SOLUSDT", name: "Solana", short: "SOL/USDT" },
    { symbol: "XRPUSDT", name: "XRP", short: "XRP/USDT" },
    { symbol: "ADAUSDT", name: "Cardano", short: "ADA/USDT" },
    { symbol: "DOGEUSDT", name: "Dogecoin", short: "DOGE/USDT" },
    { symbol: "DOTUSDT", name: "Polkadot", short: "DOT/USDT" },
    { symbol: "MATICUSDT", name: "Polygon", short: "MATIC/USDT" },
    { symbol: "LTCUSDT", name: "Litecoin", short: "LTC/USDT" },
    { symbol: "AVAXUSDT", name: "Avalanche", short: "AVAX/USDT" },
    { symbol: "ATOMUSDT", name: "Cosmos", short: "ATOM/USDT" },
  ];

  const filteredPairs = popularPairs.filter(pair => 
    pair.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    pair.short.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedPair = popularPairs.find(p => p.symbol === selectedSymbol);

  return (
    <div className="min-h-screen bybit-grid-subtle">
      {/* Header */}
      <header className="border-b border-[#1F1F1F] bg-[#000000] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/dashboard")}
                className="text-[#A1A1AA] hover:text-white transition-colors"
              >
                <ArrowLeft size={24} />
              </button>
              <h1 className="font-bold text-xl text-white">Онлайн-рынок</h1>
            </div>
            <Link
              to="/dashboard"
              className="text-[#EDEDED] hover:text-[#EDEDED] transition-colors font-medium"
            >
              Дашборд
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Sidebar - Pairs List */}
          <div className="lg:col-span-1">
            <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] overflow-hidden sticky top-24">
              <div className="p-4 border-b border-[#1F1F1F]">
                <h2 className="font-bold text-white mb-3">Торговые пары</h2>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Поиск..."
                    className="w-full bg-[#000000] border border-[#1F1F1F] rounded pl-9 pr-3 py-2 text-white text-sm placeholder-[#A1A1AA] focus:border-white focus:outline-none"
                  />
                </div>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {filteredPairs.map((pair) => (
                  <button
                    key={pair.symbol}
                    onClick={() => setSelectedSymbol(pair.symbol)}
                    className={`w-full px-4 py-3 flex items-center justify-between hover:bg-[#1A1A1A] transition-colors border-b border-[#1F1F1F]/30 ${
                      selectedSymbol === pair.symbol ? "bg-white/10 border-l-2 border-l-white" : ""
                    }`}
                  >
                    <div className="text-left">
                      <div className={`font-medium ${selectedSymbol === pair.symbol ? "text-[#EDEDED]" : "text-white"}`}>
                        {pair.short}
                      </div>
                      <div className="text-[#A1A1AA] text-xs">{pair.name}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Chart */}
          <div className="lg:col-span-4">
            <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] overflow-hidden">
              <div className="p-4 border-b border-[#1F1F1F] flex items-center justify-between">
                <div>
                  <h2 className="font-bold text-white text-lg">
                    {selectedPair?.short || selectedSymbol}
                  </h2>
                  <p className="text-[#A1A1AA] text-sm">
                    {selectedPair?.name} • Binance
                  </p>
                </div>
                <div className="flex gap-2">
                  <span className="bg-[#34D399]/20 text-[#34D399] text-xs px-2 py-1 rounded font-medium">LIVE</span>
                </div>
              </div>
              
              {/* TradingView Chart Widget via iframe */}
              <div style={{ height: "600px" }}>
                <iframe
                  key={selectedSymbol}
                  src={`https://s.tradingview.com/widgetembed/?frameElementId=tradingview_widget&symbol=BINANCE:${selectedSymbol}&interval=60&hidesidetoolbar=0&symboledit=1&saveimage=0&toolbarbg=f1f3f6&studies=[]&theme=dark&style=1&timezone=Europe/Moscow&locale=ru&utm_source=&utm_medium=widget&utm_campaign=chart`}
                  style={{ width: "100%", height: "100%", border: "none" }}
                  allowFullScreen
                />
              </div>
            </div>

            {/* Ticker Tape */}
            <div className="mt-6 bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] overflow-hidden">
              <iframe
                src="https://s.tradingview.com/embed-widget/ticker-tape/?locale=ru&symbols=%5B%7B%22proName%22%3A%22BINANCE%3ABTCUSDT%22%2C%22title%22%3A%22Bitcoin%22%7D%2C%7B%22proName%22%3A%22BINANCE%3AETHUSDT%22%2C%22title%22%3A%22Ethereum%22%7D%2C%7B%22proName%22%3A%22BINANCE%3ABNBUSDT%22%2C%22title%22%3A%22BNB%22%7D%2C%7B%22proName%22%3A%22BINANCE%3ASOLUSDT%22%2C%22title%22%3A%22Solana%22%7D%2C%7B%22proName%22%3A%22BINANCE%3AXRPUSDT%22%2C%22title%22%3A%22XRP%22%7D%2C%7B%22proName%22%3A%22BINANCE%3AADAUSDT%22%2C%22title%22%3A%22Cardano%22%7D%2C%7B%22proName%22%3A%22BINANCE%3ADOGEUSDT%22%2C%22title%22%3A%22Doge%22%7D%5D&showSymbolLogo=true&colorTheme=dark&isTransparent=true&displayMode=adaptive"
                style={{ width: "100%", height: "78px", border: "none" }}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Market;
